/**
 * Loads the scripts a page needs, when it needs them.
 *
 * The nav fragment loads only what every page uses. A page brings its own
 * scripts as deferred <script> tags (the card page: the runtime, its fields,
 * the editor in Edit, the widgets when it has widget parts), so a full page
 * load runs them in order before _hyperscript. After an HTMX boost those tags
 * never run (htmx-security.js turns allowScriptTags off), so this file:
 *
 *   - offers window.wyldcardsLoad(names) -> Promise, which loads scripts by
 *     name ('help-center', '/js/x.js') or bundle ('card', 'edit'), each once,
 *     in order, with this build's ?v=;
 *   - holds a boosted request for a page that needs the card runtime until the
 *     runtime has loaded, so the card's scripts find window.wyldcards when
 *     _hyperscript runs them on the swap; a pointer or focus on such a link
 *     starts the download early;
 *   - starts a boosted card's widgets at the swap and holds the swap's
 *     htmx:load and the runtime's openCard until they have run (ready());
 *   - loads the rest of a boosted page's scripts once its body has settled, so
 *     they initialise against the new page (this was page-scripts.js);
 *   - falls back to a full page load if a response needs the runtime that is
 *     somehow not there yet (a form that redirects to a card, say), and reloads
 *     when a response comes from a newer build.
 *
 * scripts/test-script-bundles.cjs keeps the lists here equal to the tags the
 * templates write.
 */
(function () {
    'use strict';

    if (window.wyldcardsLoad) return;

    var me = document.currentScript;
    var versionMeta = document.querySelector('meta[name="app-asset-version"]');
    var version = versionMeta ? versionMeta.getAttribute('content') : ((me && /[?&]v=([^&#]+)/.exec(me.src)) || [])[1];

    /**
     * The card page's scripts, in the order stack/card.html writes them. 'card' must be
     * running before a card's body is swapped in; the others set themselves up on the page
     * they find, so a boosted visit may load them just after the swap.
     */
    var BUNDLES = {
        card: [
            'markdown-fields', 'card-flow', 'text-flow', 'card-rename', 'card-commands', 'card-reorder', 'card-navigator', 'stack-share',
            'wyldcards-runtime', 'script-editor', 'script-debugger', 'card-swipe',
            'wyldcards-data', 'rich-text-fields', 'paragraph-fields',
            'paragraph-spelling', 'focus-mode', 'text-only-mode', 'cards-only-mode', 'linked-cards-fields', 'hyperlink-fields', 'live-card',
            'card-auto-resize', 'field-overflow-cue', 'context-menu', 'wyldcards-history',
            'card-edit-entry', 'card-prefetch', 'card-browse', 'card-browse-views', 'command-canvas'
        ],
        widgets: ['wyldcards-widgets'],
        edit: ['wyldcards-editor', 'card-transfer-drag', 'live-card-pane'],
        paint: ['paint-mode'],
        draw: ['draw-mode']
    };
    /**
     * What a boosted request for a card page waits for: the runtime, then _hyperscript, which
     * attaches the card's handlers as the page is swapped in (Home and a few other pages do not
     * load it). Both have to be running before the swap, or the card's init and openCard find
     * nothing to call or nothing listening.
     */
    var BEFORE_SWAP = ['card', '/js/_hyperscript.min.js'];
    /**
     * Arriving after the swap, these hold the card's scripts back until they have run: the
     * swap's htmx:load (where _hyperscript runs init blocks and wires handlers) and the
     * runtime's openCard wait for them (wyldcardsLoad.ready()), so a script that reaches a
     * widget as the card opens finds it drawn, as on a full page load.
     */
    var HOLD_SCRIPTS = ['widgets'];

    /** pathname -> Promise that settles once that script has run. */
    var scripts = {};
    /** pathnames that have run. */
    var ran = {};

    function pathOf(name) {
        if (name.indexOf('/') !== -1) {
            try {
                var url = new URL(name, window.location.href);
                return url.origin === window.location.origin ? url.pathname : null;
            } catch (error) {
                return null;
            }
        }
        return '/js/' + name.replace(/\.js$/, '') + '.js';
    }

    function expand(names) {
        var list = [];
        [].concat(names).forEach(function (name) {
            (BUNDLES[name] || [name]).forEach(function (one) {
                var path = pathOf(one);
                if (path && list.indexOf(path) === -1) list.push(path);
            });
        });
        return list;
    }

    /* The page's own tags: deferred ones run, in order, before DOMContentLoaded. */
    var parsed = document.readyState === 'loading'
        ? new Promise(function (resolve) { document.addEventListener('DOMContentLoaded', resolve, { once: true }); })
        : Promise.resolve();
    Array.prototype.forEach.call(document.scripts, function (script) {
        var path = script.src && pathOf(script.src);
        if (!path || scripts[path]) return;
        scripts[path] = parsed.then(function () { ran[path] = true; });
        if (document.readyState !== 'loading') ran[path] = true;
    });

    function loadOne(path) {
        if (scripts[path]) return scripts[path];
        scripts[path] = new Promise(function (resolve, reject) {
            var tag = document.createElement('script');
            tag.src = path + (version ? '?v=' + encodeURIComponent(version) : '');
            /* Inserted scripts run in the order they were added, like deferred tags. */
            tag.async = false;
            tag.onload = function () {
                ran[path] = true;
                resolve();
            };
            tag.onerror = function () {
                delete scripts[path];
                tag.remove();
                reject(new Error('Could not load ' + path));
            };
            document.head.appendChild(tag);
        });
        return scripts[path];
    }

    /** Loads scripts or bundles by name; resolves once all of them have run. */
    function wyldcardsLoad(names) {
        return Promise.all(expand(names).map(loadOne)).then(function () { return undefined; });
    }

    /** True once every script of these names or bundles has run. */
    wyldcardsLoad.loaded = function (names) {
        return expand(names).every(function (path) { return ran[path]; });
    };
    wyldcardsLoad.bundles = BUNDLES;
    /* A Promise that settles once the scripts a boosted swap is waiting for have run, or null
       when it waits for none (so callers can go on synchronously, as before). */
    var holding = null;
    wyldcardsLoad.ready = function () {
        return holding;
    };
    window.wyldcardsLoad = wyldcardsLoad;

    /* ---- Boosted navigation ---- */

    /** What a URL is likely to open: a card page (in which mode) or nothing we know. */
    function predict(href) {
        var url;
        try {
            url = new URL(href, window.location.href);
        } catch (error) {
            return [];
        }
        if (url.origin !== window.location.origin) return [];
        var match = /^\/stack\/([^/]+)(?:\/card\/[^/]+(?:\/(?:next|prev))?|\/storyboard)?\/?$/.exec(url.pathname);
        if (!match || /^(list|new|templates|import|trash|folder|folders|search|recent)$/.test(match[1])) return [];
        var wanted = BEFORE_SWAP.slice();
        var mode = url.searchParams.get('mode');
        /* Edit brings the widgets too: a widget part can be added there. */
        if (mode === 'edit') wanted.push('widgets', 'edit');
        else if (mode === 'paint' || mode === 'draw') wanted.push(mode);
        return wanted;
    }

    function missing(names) {
        return names.filter(function (name) { return !wyldcardsLoad.loaded(name); });
    }

    /* A boosted request for a card page waits for the runtime (and the mode's tools). */
    document.addEventListener('htmx:confirm', function (event) {
        var detail = event.detail;
        if (!detail || detail.target !== document.body) return;
        var verb = String(detail.verb || 'get').toLowerCase();
        if (verb !== 'get') return;
        var wanted = missing(predict(detail.path));
        if (!wanted.length) return;
        event.preventDefault();
        var go = function () { detail.issueRequest(false); };
        wyldcardsLoad(wanted).then(go, go);
    });

    /* A pointer or focus on a card link starts the downloads the click will wait for. */
    function warm(event) {
        var link = event.target && event.target.closest && event.target.closest('a[href]');
        if (!link || link.getAttribute('hx-boost') === 'false') return;
        var wanted = missing(predict(link.href));
        if (wanted.length) wyldcardsLoad(wanted).catch(function () { /* Tried again on the click. */ });
    }
    document.addEventListener('pointerover', warm, { passive: true });
    document.addEventListener('focusin', warm);

    function responseVersion(html) {
        var match = /<meta\b[^>]*\bname=["']app-asset-version["'][^>]*\bcontent=["']([^"']+)["'][^>]*>/i.exec(html);
        return match ? match[1] : null;
    }

    /** The /js/ scripts a response's HTML brings, in order. */
    function responseScripts(html) {
        var found = [];
        var pattern = /<script\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/gi;
        var match;
        while ((match = pattern.exec(html))) {
            var path = pathOf(match[1].replace(/&amp;/g, '&'));
            if (path && path.indexOf('/js/') === 0 && found.indexOf(path) === -1) found.push(path);
        }
        return found;
    }

    var pending = null;

    document.addEventListener('htmx:beforeSwap', function (event) {
        var detail = event.detail;
        if (!detail || !detail.boosted || typeof detail.serverResponse !== 'string') return;
        var next = responseVersion(detail.serverResponse);
        /* A response from a newer build may need newer scripts: load it whole, once. */
        if (version && next && version !== next) {
            detail.shouldSwap = false;
            window.location.reload();
            return;
        }
        var brought = responseScripts(detail.serverResponse);
        var wanted = brought.filter(function (path) { return !ran[path]; });
        var card = expand('card');
        var cardPage = brought.some(function (path) { return card.indexOf(path) !== -1; });
        var early = cardPage ? expand(BEFORE_SWAP) : [];
        var tooLate = wanted.some(function (path) { return early.indexOf(path) !== -1; });
        if (tooLate) {
            /* The card's scripts would run before the runtime: open the page with a full load instead. */
            var xhr = detail.xhr;
            detail.shouldSwap = false;
            var destination = (xhr && xhr.responseURL) || detail.pathInfo && detail.pathInfo.finalRequestPath || window.location.href;
            // XHR response URLs omit fragments; keep the requested Settings section (as htmx does).
            var anchor = detail.pathInfo && detail.pathInfo.anchor;
            if (anchor && destination.indexOf('#') === -1) destination += '#' + anchor;
            window.location.assign(destination);
            return;
        }
        var hold = expand(HOLD_SCRIPTS);
        var now = wanted.filter(function (path) { return hold.indexOf(path) !== -1; });
        pending = wanted.filter(function (path) { return now.indexOf(path) === -1; });
        if (now.length) {
            /* Started now, and the swap's scripts wait for them (see HOLD_SCRIPTS). */
            var release = function () { holding = null; replayLoads(); };
            holding = Promise.all(now.map(loadOne)).then(release, release);
        }
    });

    /* While held, the swap's htmx:load events wait and are sent again once the scripts have run. */
    var heldLoads = [];
    document.addEventListener('htmx:load', function (event) {
        if (!holding || event.wyldcardsReplay) return;
        event.stopImmediatePropagation();
        heldLoads.push(event.target);
    }, true);

    function replayLoads() {
        var targets = heldLoads;
        heldLoads = [];
        targets.forEach(function (target) {
            if (!target.isConnected) return;
            var replay = new CustomEvent('htmx:load', { bubbles: true, cancelable: true, detail: { elt: target } });
            replay.wyldcardsReplay = true;
            target.dispatchEvent(replay);
        });
    }

    /* A fragment that needs a script of its own names it in data-wc-script (the Code editor
       panel: 'code-editor'); the script loads once and sets the fragment up on arrival. */
    document.addEventListener('htmx:load', function (event) {
        var root = event.target;
        if (!root || !root.querySelectorAll) return;
        if (root === document.body) reopenPanel();
        var holders = Array.prototype.slice.call(root.querySelectorAll('[data-wc-script]'));
        if (root.matches && root.matches('[data-wc-script]')) holders.unshift(root);
        holders.forEach(function (holder) {
            wyldcardsLoad(holder.getAttribute('data-wc-script').split(/\s+/)).catch(function () { /* The fragment stays as sent. */ });
        });
    });

    /* A side panel that reloaded the page to show what it saved (the Code editor) asks to be opened
       again: sessionStorage holds its URL and the page it belongs to, for one load. htmx sends
       htmx:load on the body once it is running, on a full load and after a boosted swap. */
    var REOPEN_KEY = 'wyldcards.reopenPanel';
    function reopenPanel() {
        var wanted = null;
        try {
            wanted = JSON.parse(window.sessionStorage.getItem(REOPEN_KEY) || 'null');
            window.sessionStorage.removeItem(REOPEN_KEY);
        } catch (e) { wanted = null; }
        var side = document.getElementById('wyldcards-side');
        if (!wanted || !side || wanted.path !== window.location.pathname || typeof htmx === 'undefined') return;
        htmx.ajax('GET', wanted.url, { target: side, swap: 'innerHTML' });
    }

    document.addEventListener('htmx:afterSettle', function () {
        if (!pending) return;
        var wanted = pending;
        pending = null;
        wanted.forEach(function (path) {
            loadOne(path).catch(function () { /* A missing page script leaves its page as before. */ });
        });
    });
})();
